/* eslint-disable react/state-in-constructor */
import React, {Component} from "react";
import {getCookie, removePipes} from "@core-ui/utility";
import {withBreakpointData} from "@core-ui/breakpoint-provider";
import {withAppState} from "@core-ui/app-state-provider";
import {fetchPersonalization} from "./collaborators/fetch-personalization";
import dataLayerBuilder from "./datalayer/builder";

const personalizationData = {
  isLoading: true,
  isEmpty: true,
  isError: false,
  personalizationReferrerInfo: {},
  shoppingBag: {
    totalItemCount: 0,
  },
  originPathOverride: "",
  pageType: "",
  customerUUID: "",
  preScreenResponse: {preScreenId: ""},
};

export const PersonalizationContext = React.createContext(personalizationData);

const reportNREvent = (data: any) => {
  if (window && window.newrelic) {
    window.newrelic
      .interaction()
      .setAttribute("JSESSIONID", getCookie("JSESSIONID") || "")
      .setAttribute(
        "unknownShopperId",
        removePipes(getCookie("unknownShopperId"))
      )
      .setAttribute("s_vi", getCookie("s_vi") || "")
      .save();
  }
  return data;
};

export type PersonalizationProviderProps = {
  breakpointData: {
    size: string;
  };
  children?: React.ReactNode;
  datalayer: Record<string, unknown>;
  errorLogger: (error: any) => void;
  marketAgnosticBrandCode: number;
  originPathOverride: string;
  pageType: string;
};

class PersonalizationProviderClass extends Component {
  constructor(props: PersonalizationProviderProps) {
    super(props);
    const {pageType} = props;

    this.state = {value: {...personalizationData, pageType}};
    this.getData = this.getData.bind(this);
    this.updateBagCount = this.updateBagCount.bind(this);
  }

  componentDidMount() {
    document.addEventListener(
      "updateBagCount",
      (e) => {
        this.updateBagCount(e);
      },
      false
    );

    const dataPromise = this.getData();
    const {datalayer = {}} = this.props as PersonalizationProviderProps;

    dataLayerBuilder({
      datalayer,
      dataPromise,
    });
  }

  shouldComponentUpdate(
    nextProps: PersonalizationProviderProps,
    nextState: PersonalizationProviderClass["state"] & {value: any}
  ) {
    // Allow for Object deep comparison instead of shallow

    const stateChange =
      JSON.stringify(nextState) !== JSON.stringify(this.state);
    return nextProps !== this.props || stateChange;
  }

  componentWillUnmount() {
    document.removeEventListener("updateBagCount", this.updateBagCount);
  }

  getData() {
    const {
      errorLogger,
      marketAgnosticBrandCode = 1,
      breakpointData = {
        size: "",
      },
      originPathOverride,
      pageType,
    } = this.props as PersonalizationProviderProps;

    return fetchPersonalization({
      marketAgnosticBrandCode,
      errorLogger,
      breakpointData,
      originPathOverride,
      pageType,
    })
      ?.then(reportNREvent)
      .then((data: any) => {
        this.setState(
          (
            prevState: PersonalizationProviderClass["state"] & {value: any}
          ) => ({
            value: {
              ...prevState.value,
              ...data,
              isLoading: false,
              isEmpty: false,
              isError: false,
            },
          })
        );
        return data;
      })
      .catch(() => {
        this.setState(
          (
            prevState: PersonalizationProviderClass["state"] & {value: any}
          ) => ({
            ...prevState,
            value: {
              ...prevState.value,
              isError: true,
              isLoading: false,
            },
          })
        );
      });
  }

  updateBagCount(data: any) {
    this.setState(
      (prevState: PersonalizationProviderClass["state"] & {value: any}) => {
        const shoppingBag = {
          totalItemCount: data.detail.quantity,
          totalItemAmount: prevState.value.shoppingBag.totalItemAmount,
        };
        return {
          value: {
            ...prevState.value,
            shoppingBag,
          },
        };
      }
    );
  }

  render() {
    const {children} = this.props;
    const {value} = this.state as PersonalizationProviderClass["state"] & {
      value: any;
    };

    return (
      <PersonalizationContext.Provider value={value}>
        {children}
      </PersonalizationContext.Provider>
    );
  }
}

function selectPageType(state: any) {
  return {pageType: state.pageType};
}

export const PersonalizationProvider: React.ElementType = withAppState(
  selectPageType
)(withBreakpointData(PersonalizationProviderClass as any));
